import { type DiagStatus, diagnose } from "./diagnose";

/**
 * The minimal user shape the attention logic (and the pages that render it)
 * actually read. Declared locally so this util no longer depends on generated
 * operation-result types; the live `users` list query returns a superset, so
 * its rows remain assignable to this type.
 */
export type AttentionUser = {
  uuid: string;
  email?: string | null;
  emailVerified?: boolean | null;
  impersonateOnly?: boolean | null;
  invitationOtpValidUntil?: unknown;
  /** Usage signals — only the merge preview reads these, to pick the live account. */
  lastLogin?: string | null;
  passwordChangedAt?: string | null;
  twoFactorMethods?: readonly unknown[] | null;
  profiles?:
    | readonly {
        uuid: string;
        listName?: string | null;
        organization?: { uuid: string; name?: string | null } | null;
      }[]
    | null;
};

/** The distinct problem categories surfaced on the dashboard / attention page. */
export type AttentionKind = "duplicateEmail" | "uppercaseEmail" | "loginIssue";

export type AttentionProblem = {
  user: AttentionUser;
  kind: AttentionKind;
  status: DiagStatus;
  issue: string;
};

/** Display order + labels + severity per kind, shared by the dashboard summary
 *  and the attention page so they can't drift. Tone values are valid StatusKinds. */
export const ATTENTION_KIND_ORDER: AttentionKind[] = [
  "duplicateEmail",
  "loginIssue",
  "uppercaseEmail",
];

export const ATTENTION_KIND_LABEL: Record<AttentionKind, string> = {
  duplicateEmail: "E-Mail mehrfach vergeben",
  loginIssue: "Anmeldung nicht abgeschlossen",
  uppercaseEmail: "E-Mail nicht klein geschrieben",
};

export const ATTENTION_KIND_TONE: Record<AttentionKind, "err" | "warn"> = {
  duplicateEmail: "err",
  loginIssue: "warn",
  uppercaseEmail: "warn",
};

/** Count problems per kind, in display order (kinds with 0 are omitted). */
export function groupAttention(
  problems: readonly AttentionProblem[],
): { kind: AttentionKind; count: number }[] {
  const counts = new Map<AttentionKind, number>();
  for (const p of problems) {
    counts.set(p.kind, (counts.get(p.kind) ?? 0) + 1);
  }
  return ATTENTION_KIND_ORDER.filter((k) => (counts.get(k) ?? 0) > 0).map((k) => ({
    kind: k,
    count: counts.get(k) ?? 0,
  }));
}

/** A fully set-up account can actually sign in: e-mail confirmed, no open invite. */
export function isFullySetUp(u: AttentionUser): boolean {
  return Boolean(u.emailVerified) && !u.invitationOtpValidUntil;
}

export type DuplicateGroup = {
  email: string;
  users: AttentionUser[];
  readyCount: number;
  /**
   * Auto-mergeable when at most one account is fully set up: the rest can be
   * merged onto it (or onto any one, if none are ready). Two+ ready accounts
   * need manual review — the dependent products must be checked first.
   */
  mergeable: boolean;
};

/** Bucket the duplicate-e-mail problems by their shared (lowercased) address and
 *  classify each bucket by how many accounts are fully set up. */
export function groupDuplicates(problems: readonly AttentionProblem[]): DuplicateGroup[] {
  const byEmail = new Map<string, AttentionUser[]>();
  for (const p of problems) {
    if (p.kind !== "duplicateEmail") {
      continue;
    }
    const key = (p.user.email ?? "").trim().toLowerCase();
    const arr = byEmail.get(key) ?? [];
    arr.push(p.user);
    byEmail.set(key, arr);
  }
  return [...byEmail.entries()]
    .map(([email, users]) => {
      const readyCount = users.filter(isFullySetUp).length;
      return { email, users, readyCount, mergeable: readyCount <= 1 };
    })
    .sort((a, b) => a.email.localeCompare(b.email));
}

function diagnoseUser(user: AttentionUser): { worst: DiagStatus; firstIssue?: string } {
  const org = user.profiles?.[0]?.organization;
  const { checks, worst } = diagnose({
    emailVerified: Boolean(user.emailVerified),
    invitePending: Boolean(user.invitationOtpValidUntil),
    impersonateOnly: Boolean(user.impersonateOnly),
    email: user.email ?? undefined,
    passwordChangedAt: user.passwordChangedAt,
    twoFactor: "none",
    federation: null,
    profiles: [{ orgId: org?.uuid ?? "" }],
  });
  return { worst, firstIssue: checks.find((c) => c.status !== "ok")?.label };
}

/**
 * The single source of truth for "Braucht Aufmerksamkeit" / "Mit Problem".
 * Real account-hygiene issues count even during an open invitation:
 *  - a duplicate e-mail (same address on >1 account → the account can't log in)
 *  - an e-mail that isn't all-lowercase (can cause login mismatches)
 * A bare open invitation on its own is expected (the person just hasn't accepted
 * yet), so it's skipped — unless the account has no e-mail address, in which
 * case the invitation can never have been sent. Anything else falls back to the
 * login diagnosis.
 */
export function computeAttention(users: readonly AttentionUser[]): AttentionProblem[] {
  const emailCount = new Map<string, number>();
  for (const u of users) {
    const e = u.email?.trim().toLowerCase();
    if (e) {
      emailCount.set(e, (emailCount.get(e) ?? 0) + 1);
    }
  }
  const out: AttentionProblem[] = [];
  for (const u of users) {
    const email = u.email ?? "";
    const lower = email.trim().toLowerCase();
    if (lower && (emailCount.get(lower) ?? 0) > 1) {
      out.push({
        user: u,
        kind: "duplicateEmail",
        status: "err",
        issue: "E-Mail mehrfach vergeben",
      });
      continue;
    }
    if (email && email !== email.toLowerCase()) {
      out.push({
        user: u,
        kind: "uppercaseEmail",
        status: "warn",
        issue: "E-Mail nicht klein geschrieben",
      });
      continue;
    }
    // An open invitation *with* an address is the expected in-between state.
    // Without one it can never have been delivered, so it falls through to the
    // diagnosis, which reports it as an error.
    if (u.invitationOtpValidUntil && email) {
      continue;
    }
    const d = diagnoseUser(u);
    if (d.worst !== "ok") {
      out.push({
        user: u,
        kind: "loginIssue",
        status: d.worst,
        issue: d.firstIssue ?? "Prüfung fehlgeschlagen",
      });
    }
  }
  return out;
}
